import { forwardRef, useState } from 'react'

import classNames from 'classnames'
import { Group } from 'globalTypes'
import Autosuggest from 'react-autosuggest'
import TagsInput from 'react-tagsinput'

import { gql, useMutation } from '@apollo/client'
import { Trans, msg } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import Button from 'Root/App/Form/Button'
import Paragraph from 'Root/App/Paragraph'
import { MAX_TAGS } from 'lib/constants'

import autosuggest from './AddUser/autosuggest.module.css'
import styles from './add-form.module.css'
import GroupInfo from './group-info'
import GroupTag from './group-tag'

const MAX_TAGS_INPUT_LENGTH = 3000

const ADD_AUTHORIZATION_TO_GROUPS = gql`
  mutation AddAuthorizationsGroups($elementId: ID!, $groupsIds: [ID!]!) {
    addAuthorizationsGroups(elementId: $elementId, groupsIds: $groupsIds)
  }
`

interface GroupAutosuggestProps {
  workspaceGroups: Group[]
  ref?: React.Ref<HTMLInputElement>
  [props: string]: any
}

const GroupSuggestion = (suggestion) => {
  const name = suggestion.name ?? 'G'
  const identifier = suggestion.identifier ?? ''

  return (
    <GroupInfo
      name={name}
      identifier={identifier}
      usersCount={suggestion.usersCount}
    />
  )
}

const GroupAutosuggest = forwardRef<HTMLInputElement, GroupAutosuggestProps>(
  ({ addTag, workspaceGroups, ...props }, ref) => {
    const [suggestions, setSuggestions] = useState([])

    const handleOnChange = (e, { method }) => {
      if (method === 'enter') {
        e.preventDefault()
      } else {
        props.onChange(e)
      }
    }

    const onSuggestionSelected = (_e, { suggestion }) => {
      addTag({
        name: suggestion.name,
        id: suggestion.id,
        identifier: suggestion.identifier,
        usersCount: suggestion.usersCount,
      })
    }

    const onSuggestionsFetchRequested = ({ value }) =>
      setSuggestions(getSuggestions(value))

    const getSuggestions = (value) => {
      const inputValue = value.trim().toLowerCase()
      const inputLength = inputValue.length

      return inputLength === 0
        ? []
        : workspaceGroups.filter(
            (g) =>
              g.name.toLowerCase().slice(0, inputLength) === inputValue ||
              g.identifier.toLowerCase().slice(0, inputLength) === inputValue
          )
    }

    return (
      <Autosuggest
        getSuggestionValue={(suggestion) => suggestion.name}
        inputProps={{ ...props, onChange: handleOnChange }}
        onSuggestionsClearRequested={() => {}}
        onSuggestionSelected={onSuggestionSelected}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        ref={ref}
        renderSuggestion={GroupSuggestion}
        suggestions={suggestions}
        theme={autosuggest}
      />
    )
  }
)

GroupAutosuggest.displayName = 'GroupAutosuggest'

const AddGroup = ({ elementId, workspaceGroups }) => {
  const { _ } = useLingui()
  const [groups, setGroups] = useState({ tags: [] })
  const [tooManyGroupsWarning, setTooManyGroupsWarning] = useState(false)
  const [invalidGroupsWarning, setInvalidGroupsWarning] = useState(false)
  const [mutate, { loading }] = useMutation(ADD_AUTHORIZATION_TO_GROUPS, {
    refetchQueries: ['GetElementAuthorizationForGroups'],
  })

  const handleTagsChange = (tags) => {
    let validTags = []
    let invalidTagsCount = 0

    if (tags.length > MAX_TAGS) {
      setTooManyGroupsWarning(true)
      return
    }

    validTags = tags
      .map((tag, index) => {
        if (typeof tag === 'string') {
          // Group has been typed or pasted, so other info are missing and must be recovered
          const normalizedTag = tag.trim().toLowerCase()

          const found = workspaceGroups.find((g) => {
            const name = (g.name ?? '').toLowerCase()
            const identifier = (g.identifier ?? '').toLowerCase()
            return name === normalizedTag || identifier === normalizedTag
          })

          if (!found) {
            invalidTagsCount += 1
          }

          return found
            ? { ...found }
            : { name: tag, id: `invalid_group_${index + 1}` }
        } else if (typeof tag === 'object' && tag.name && tag.id) {
          // Group has been selected from the suggestion list, so the tag has all the info relative to the group
          if (tag.id.includes('invalid')) {
            invalidTagsCount += 1
          }
          return tag
        }
        return null
      })
      .filter(Boolean)

    // Remove duplicates based on id.
    validTags = Array.from(
      new Map(validTags.map((tag) => [tag.id, tag])).values()
    )

    const tagsToSet = validTags.length > 0 ? validTags : []

    setTooManyGroupsWarning(false)
    setInvalidGroupsWarning(Boolean(invalidTagsCount > 0))
    setGroups({ tags: tagsToSet })
  }

  const renderInput = ({ addTag, ...props }) => (
    <GroupAutosuggest
      addTag={addTag}
      {...props}
      workspaceGroups={workspaceGroups}
    />
  )

  const renderTag = ({ key, tag, ...props }) => (
    <GroupTag {...props} index={key} key={key} tag={tag} />
  )

  const isValid = groups.tags.length > 0

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!isValid) return

    const groupIds = groups.tags
      .map((tag) => tag.id)
      .filter((groupId) => !groupId.includes('invalid'))

    try {
      await mutate({
        variables: {
          elementId: elementId,
          groupsIds: groupIds,
        },
      })
    } catch (error) {
      console.error('Error adding groups:', error)
    }

    setGroups({ tags: [] })
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <TagsInput
        addKeys={[9, 13, 32, 59, 108, 188, 222]}
        className={styles.tagsInput}
        inputProps={{
          placeholder: _(msg`Invita o aggiungi gruppi...`),
          disabled: groups?.tags?.length > MAX_TAGS,
        }}
        onChange={handleTagsChange}
        onlyUnique
        addOnPaste
        pasteSplit={(data) => {
          const parts = data
            .split(/[\n,;]+/)
            .map((s) => s.trim())
            .filter(Boolean)
            .map((s) =>
              s.length > MAX_TAGS_INPUT_LENGTH
                ? s.slice(0, MAX_TAGS_INPUT_LENGTH)
                : s
            )

          return parts.length > 0
            ? parts
            : [data.trim().slice(0, MAX_TAGS_INPUT_LENGTH)]
        }}
        renderTag={renderTag}
        renderInput={renderInput}
        tagDisplayProperty="name"
        value={groups.tags}
      />

      <div className="mb-4">
        <div className="flex items-center w-full gap-3">
          <div className="flex flex-col gap-2">
            {tooManyGroupsWarning && (
              <div className="text-red-500 text-sm text-left">
                <Trans>{`Oops, puoi inserire max ${MAX_TAGS} gruppi per volta.`}</Trans>
              </div>
            )}
            {invalidGroupsWarning && (
              <Paragraph className="text-red-500">
                <Trans>
                  Oops, i gruppi evidenziati non sono validi e verranno esclusi.
                </Trans>
              </Paragraph>
            )}
          </div>

          <label
            className={classNames([
              'text-right text-xs ml-auto',
              {
                'text-red-500': groups?.tags?.length === MAX_TAGS,
                'text-gray-500': groups?.tags?.length < MAX_TAGS,
              },
            ])}
          >
            {groups?.tags?.length ?? 0}/{MAX_TAGS}
          </label>
        </div>
      </div>

      <Button
        disabled={!isValid || loading}
        processing={loading}
        size="XS"
        type="submit"
      >
        <Trans>Condividi</Trans>
      </Button>
    </form>
  )
}

export default AddGroup
